import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Banner, Button, ChoiceList, Modal, Text, TextField } from '@shopify/polaris';
import { getApiBaseUrl } from '../utils/apiClient';
import {
  ARRIVAL_PANEL_TABS,
  ARRIVAL_STORE_LABELS,
  arrivalAge,
  arrivalMatchesFilter,
  arrivalMatchesStore,
  arrivalPanelSections,
  rankCandidatePos
} from '../utils/unmatchedArrivals';

const API_URL = getApiBaseUrl();

function errorText(err, fallback) {
  return err?.response?.data?.error || fallback;
}

const EMPTY_FORM = { store: 'unknown', supplier: '', carrier: '', cartons: '', notes: '' };

/** Store fills match the PO cards' drawn store pill, so the picker reads as the badge. */
const STORE_CHOICES = [
  { value: 'phaser', label: 'Phaser', fill: 'var(--brand-phaser, #e8192e)' },
  { value: 'ccc', label: 'CCC', fill: '#b45309' },
  { value: 'unknown', label: 'Not sure', fill: '#6b7280' }
];

function storeBadgeStyle(fill, selected = true) {
  return {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    padding: '3px 9px', borderRadius: '7px',
    fontSize: '11px', fontWeight: 700, letterSpacing: '0.05em',
    textTransform: 'uppercase', whiteSpace: 'nowrap', lineHeight: 1.2,
    border: `2px solid ${selected ? fill : 'var(--app-border-strong)'}`,
    background: selected ? fill : 'transparent',
    color: selected ? '#ffffff' : 'var(--app-ink-secondary)'
  };
}

function storeFill(store) {
  return (STORE_CHOICES.find((c) => c.value === store) || STORE_CHOICES[2]).fill;
}

function StorePicker({ value, onChange }) {
  return (
    <div role="radiogroup" aria-label="Store" style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
      {STORE_CHOICES.map((choice) => {
        const selected = value === choice.value;
        return (
          <button
            key={choice.value}
            type="button"
            role="radio"
            aria-checked={selected}
            onClick={() => onChange(choice.value)}
            style={{ ...storeBadgeStyle(choice.fill, selected), minHeight: '36px', minWidth: '84px', fontSize: '12px', cursor: 'pointer' }}
          >
            {choice.label}
          </button>
        );
      })}
    </div>
  );
}

function LogArrivalModal({ open, onClose, onSaved, vendorNames, defaultStore }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) {
      setForm({ ...EMPTY_FORM, store: defaultStore && defaultStore !== 'all' ? defaultStore : 'unknown' });
      setError(null);
    }
  }, [open, defaultStore]);

  const set = (key) => (value) => setForm((prev) => ({ ...prev, [key]: value }));

  const canSave = Boolean(form.supplier.trim() || form.notes.trim());

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const res = await axios.post(`${API_URL}/api/unmatched-arrivals`, { ...form, photos: [] }, { withCredentials: true });
      onSaved(res.data.arrival);
    } catch (err) {
      setError(errorText(err, 'Could not save. Try again.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Goods arrived with no PO"
      primaryAction={{ content: 'Notify purchasing', onAction: save, loading: saving, disabled: !canSave }}
      secondaryActions={[{ content: 'Cancel', onAction: onClose }]}
    >
      <Modal.Section>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', color: 'var(--app-ink)' }}>
          <div style={{ fontSize: '13px', color: 'var(--app-ink-secondary)' }}>
            Put the goods aside. Purchasing gets an email to chase the invoice and raise the PO. The card stays on Incoming Stock until they link it.
          </div>
          {error && <Banner tone="critical">{error}</Banner>}
          <div>
            <div style={{ marginBottom: '6px', fontSize: '13px', fontWeight: 600 }}>Store</div>
            <StorePicker value={form.store} onChange={set('store')} />
          </div>
          <TextField label="Supplier" value={form.supplier} onChange={set('supplier')} autoComplete="off" list="unmatched-arrival-vendors" placeholder="Name on the box or packing slip" />
          <datalist id="unmatched-arrival-vendors">
            {vendorNames.map((name) => <option key={name} value={name} />)}
          </datalist>
          <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 100px', gap: '8px' }}>
            <TextField label="Carrier" value={form.carrier} onChange={set('carrier')} autoComplete="off" placeholder="TNT, DHL…" />
            <TextField label="Cartons" type="number" min={0} value={form.cartons} onChange={set('cartons')} autoComplete="off" />
          </div>
          <TextField label="Notes" value={form.notes} onChange={set('notes')} multiline={3} autoComplete="off" placeholder="What's in it, where you put it, anything odd" />
        </div>
      </Modal.Section>
    </Modal>
  );
}

function LinkPoModal({ arrival, purchaseOrders, onClose, onSaved }) {
  const candidates = useMemo(() => rankCandidatePos(arrival, purchaseOrders), [arrival, purchaseOrders]);
  const [picked, setPicked] = useState([]);
  const [typed, setTyped] = useState('');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const chosen = candidates.find((c) => c.id === picked[0]) || null;
  const canSave = Boolean(chosen || typed.trim());

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const res = await axios.post(`${API_URL}/api/unmatched-arrivals/${arrival.id}/status`, {
        status: 'resolved',
        poId: chosen ? chosen.id : null,
        poReference: chosen ? chosen.reference : typed.trim(),
        note
      }, { withCredentials: true });
      onSaved(res.data.arrival);
    } catch (err) {
      setError(errorText(err, 'Could not link. Try again.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={`Link ${arrival.supplier || 'delivery'} to its PO`}
      primaryAction={{ content: 'Link PO', onAction: save, loading: saving, disabled: !canSave }}
      secondaryActions={[{ content: 'Cancel', onAction: onClose }]}
    >
      <Modal.Section>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {error && <Banner tone="critical">{error}</Banner>}
          {candidates.length > 0 ? (
            <div style={{ maxHeight: 300, overflowY: 'auto' }}>
              <ChoiceList
                title="Loaded POs (supplier matches first)"
                choices={candidates.map((c) => ({
                  value: c.id,
                  label: `${c.reference}${c.vendor ? ` - ${c.vendor}` : ''}${c.match ? '  ★' : ''}`
                }))}
                selected={picked}
                onChange={(value) => { setPicked(value); setTyped(''); }}
              />
            </div>
          ) : (
            <Text tone="subdued" variant="bodySm">No loaded POs to pick from. Type the PO number instead.</Text>
          )}
          <TextField
            label="Or type the PO number"
            value={typed}
            onChange={(value) => { setTyped(value); if (value) setPicked([]); }}
            autoComplete="off"
            placeholder="CCC2310 / SI-8001"
          />
          <TextField label="Note (optional)" value={note} onChange={setNote} autoComplete="off" />
        </div>
      </Modal.Section>
    </Modal>
  );
}

/**
 * "No PO yet" cards on Incoming Stock: deliveries floor staff logged because
 * there was no PO to receive against. Purchasing links each one to its PO
 * once raised (or marks it not needed), which closes the card.
 *
 * The logger itself opens from the "No PO? Notify purchasing" button in the
 * Incoming Stock tab row (App.js owns logOpen), so it works on every tab.
 */
export default function UnmatchedArrivalsPanel({
  darkMode,
  statusTab,
  storeFilter,
  filterText,
  purchaseOrders,
  refreshKey,
  onOpenPo,
  logOpen,
  onLogOpenChange
}) {
  const [arrivals, setArrivals] = useState([]);
  const [loadError, setLoadError] = useState(null);
  const [linking, setLinking] = useState(null);
  const [showClosed, setShowClosed] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [toast, setToast] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/unmatched-arrivals`, { withCredentials: true });
      setArrivals(Array.isArray(res.data?.arrivals) ? res.data.arrivals : []);
      setLoadError(null);
    } catch (err) {
      setLoadError(errorText(err, 'Could not load deliveries with no PO.'));
    }
  }, []);

  useEffect(() => { load(); }, [load, refreshKey]);
  useEffect(() => {
    const timer = setInterval(load, 120000);
    return () => clearInterval(timer);
  }, [load]);
  useEffect(() => {
    if (!toast) return undefined;
    const timer = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(timer);
  }, [toast]);

  const replace = (next) => setArrivals((prev) => {
    const others = prev.filter((a) => a.id !== next.id);
    return [...others, next];
  });

  const vendorNames = useMemo(() => {
    const names = new Set();
    (purchaseOrders || []).forEach((po) => {
      const name = String(po?.vendor_display_name || po?.supplier_name || po?.vendor || '').trim();
      if (name) names.add(name);
    });
    return [...names].sort((a, b) => a.localeCompare(b)).slice(0, 300);
  }, [purchaseOrders]);

  const visible = arrivals.filter((a) => arrivalMatchesStore(a, storeFilter) && arrivalMatchesFilter(a, filterText));
  const { open, closed } = arrivalPanelSections(visible);

  const logModal = (
    <LogArrivalModal
      open={Boolean(logOpen)}
      onClose={() => onLogOpenChange?.(false)}
      vendorNames={vendorNames}
      defaultStore={storeFilter}
      onSaved={(arrival) => {
        replace(arrival);
        onLogOpenChange?.(false);
        setToast({ tone: 'success', text: 'Logged. Purchasing has been emailed.' });
      }}
    />
  );

  // Cards only show on the receiving tabs; the logger works everywhere.
  if (!ARRIVAL_PANEL_TABS.includes(statusTab)) return logModal;

  const setStatus = async (arrival, status, extra = {}) => {
    setBusyId(arrival.id);
    try {
      const res = await axios.post(`${API_URL}/api/unmatched-arrivals/${arrival.id}/status`, { status, ...extra }, { withCredentials: true });
      replace(res.data.arrival);
    } catch (err) {
      setToast({ tone: 'critical', text: errorText(err, 'Could not update. Try again.') });
    } finally {
      setBusyId(null);
    }
  };

  const dismiss = (arrival) => {
    // eslint-disable-next-line no-alert
    const note = window.prompt('Why is no PO needed? (sample, return, wrong address…)', '');
    if (note === null) return;
    setStatus(arrival, 'dismissed', { note });
  };

  const ink = darkMode ? '#e5e7eb' : '#1f2937';
  const subInk = 'var(--app-ink-secondary)';
  const pill = (bg, color) => ({
    display: 'inline-flex', alignItems: 'center', padding: '1px 9px', borderRadius: '999px',
    fontSize: '11px', fontWeight: 700, lineHeight: '16px', whiteSpace: 'nowrap', background: bg, color
  });

  const hasCards = open.length > 0;
  const hasHeader = hasCards || loadError || toast;

  return (
    <div style={{ marginBottom: hasHeader ? '12px' : 0 }} data-testid="unmatched-arrivals-panel">
      {hasHeader && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: open.length ? '8px' : 0, flexWrap: 'wrap' }}>
          {hasCards && (
            <Text variant="headingSm" as="h3">
              📦 No PO yet{open.length ? ` · ${open.length}` : ''}
            </Text>
          )}
          {closed.length > 0 && (
            <Button size="slim" variant="plain" onClick={() => setShowClosed((v) => !v)}>
              {showClosed ? 'Hide' : 'Show'} recently linked ({closed.length})
            </Button>
          )}
          {loadError && <Text tone="critical" variant="bodySm">{loadError}</Text>}
          {toast && <Text tone={toast.tone} variant="bodySm">{toast.text}</Text>}
        </div>
      )}

      {open.length > 0 && (
        <div className="po-grid">
          {open.map((arrival) => {
            const age = arrivalAge(arrival);
            const busy = busyId === arrival.id;
            return (
              <div
                key={arrival.id}
                className="po-card"
                data-testid="unmatched-arrival-card"
                style={{
                  display: 'flex', flexDirection: 'column', gap: '8px',
                  padding: '10px 12px', borderRadius: '10px',
                  border: `2px dashed ${age.color}`,
                  background: darkMode ? 'rgba(232, 115, 12, 0.10)' : '#fffaf2',
                  color: ink
                }}
              >
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                      <span style={pill('#b45309', '#fff')}>NO PO</span>
                      <Text variant="headingSm" as="span">{arrival.supplier || 'Unknown supplier'}</Text>
                    </div>
                    <div style={{ fontSize: '12px', color: subInk, marginTop: '2px' }}>
                      Logged by {arrival.createdByName || arrival.createdBy || 'someone'}
                      {arrival.createdAt ? ` · ${new Date(arrival.createdAt).toLocaleString('en-AU', { day: '2-digit', month: '2-digit', hour: 'numeric', minute: '2-digit' })}` : ''}
                    </div>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px' }}>
                    <span style={storeBadgeStyle(storeFill(arrival.store))}>
                      {arrival.store === 'phaser' ? 'Phaser' : (ARRIVAL_STORE_LABELS[arrival.store] || 'Store?')}
                    </span>
                    {age.label && <span style={pill(age.color, '#fff')} title="Time since the delivery was logged">{age.label}</span>}
                  </div>
                </div>

                {/* Tracking + photos are no longer collected (2026-10-09) but
                    older arrivals still carry them, so keep showing them. */}
                {(arrival.carrier || arrival.tracking || arrival.cartons) && (
                  <div style={{ fontSize: '12px', display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                    {arrival.carrier && <span><span style={{ color: subInk }}>Carrier </span>{arrival.carrier}</span>}
                    {arrival.tracking && <span style={{ overflowWrap: 'anywhere' }}><span style={{ color: subInk }}>Tracking </span>{arrival.tracking}</span>}
                    {arrival.cartons && <span><span style={{ color: subInk }}>Cartons </span>{arrival.cartons}</span>}
                  </div>
                )}
                {arrival.notes && (
                  <div style={{ fontSize: '13px', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{arrival.notes}</div>
                )}
                {arrival.photos?.length > 0 && (
                  <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                    {arrival.photos.map((photo) => (
                      <a key={photo.path} href={`${API_URL}${photo.url}`} target="_blank" rel="noreferrer" title="Open full size">
                        <img src={`${API_URL}${photo.url}`} alt="Packing slip" loading="lazy" style={{ width: 64, height: 64, objectFit: 'cover', borderRadius: 6, border: '1px solid rgba(128,128,128,0.35)' }} />
                      </a>
                    ))}
                  </div>
                )}

                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginTop: 'auto' }}>
                  <Button size="slim" variant="primary" onClick={() => setLinking(arrival)} disabled={busy}>Link to PO</Button>
                  {/* Native button: Polaris plain+critical paints a grey chip
                      inside the dark Incoming Stock overlay. */}
                  <button
                    type="button"
                    onClick={() => dismiss(arrival)}
                    disabled={busy}
                    style={{ background: 'none', border: 'none', padding: '4px 6px', fontSize: '13px', fontWeight: 600, color: 'var(--app-critical-ink)', cursor: busy ? 'default' : 'pointer', opacity: busy ? 0.5 : 1 }}
                  >
                    No PO needed
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showClosed && closed.length > 0 && (
        <div style={{ marginTop: '8px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
          {closed.map((arrival) => (
            <div key={arrival.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', flexWrap: 'wrap' }}>
              <span style={pill(arrival.status === 'resolved' ? '#1a874f' : '#6b7280', '#fff')}>
                {arrival.status === 'resolved' ? 'LINKED' : 'NO PO NEEDED'}
              </span>
              <span style={{ fontWeight: 600 }}>{arrival.supplier || 'Unknown supplier'}</span>
              {arrival.resolution?.poReference && <span>→ {arrival.resolution.poReference}</span>}
              {arrival.resolution?.note && <span style={{ color: subInk }}>{arrival.resolution.note}</span>}
              <span style={{ color: subInk }}>by {arrival.resolvedByName || arrival.resolvedBy || 'someone'}</span>
              {arrival.resolution?.poReference && onOpenPo && (
                <Button size="micro" variant="plain" onClick={() => onOpenPo(arrival.resolution.poReference)}>Open PO</Button>
              )}
              <Button size="micro" variant="plain" onClick={() => setStatus(arrival, 'open')} disabled={busyId === arrival.id}>Reopen</Button>
            </div>
          ))}
        </div>
      )}

      {logModal}
      {linking && (
        <LinkPoModal
          arrival={linking}
          purchaseOrders={purchaseOrders}
          onClose={() => setLinking(null)}
          onSaved={(arrival) => { replace(arrival); setLinking(null); }}
        />
      )}
    </div>
  );
}
